import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Play, ChevronDown, ChevronRight, Loader2, AlertTriangle, Settings2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Modal } from '@/components/ui/modal';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/stores/toast-store';
import { formatDateTime } from '@/lib/utils';
import { useAdminTenants } from '@/hooks/use-admin';
import {
  useAutoPoConfigs,
  useAutoPoConfigRuns,
  useCreateAutoPoConfig,
  useUpdateAutoPoConfig,
  useDeleteAutoPoConfig,
  useTriggerAutoPoConfig,
  useTenantPayeesForAutoPo,
  useTenantLocationsForAutoPo,
  type AutoPoConfig,
  type AutoPoConfigRun,
  type CreateAutoPoConfigInput,
  type UpdateAutoPoConfigInput,
} from '@/hooks/use-admin-auto-po';

// ── Constants ────────────────────────────────────────────────────────────────

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
] as const;

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'UTC', label: 'UTC' },
] as const;

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sun' },
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
] as const;

const DAYS_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const RUN_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'default'> = {
  success: 'success',
  completed: 'success',
  skipped: 'warning',
  failed: 'destructive',
  error: 'destructive',
  running: 'info' as any,
  pending: 'warning',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatFrequency(frequency: string): string {
  return frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();
}

function parseCsvNumbers(csv: string | null | undefined): number[] {
  if (!csv) return [];
  return csv
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

function formatScheduleSummary(config: AutoPoConfig): string {
  const parts: string[] = [];
  if (config.scheduleTime) parts.push(config.scheduleTime);
  if (config.timezone) parts.push(config.timezone.replace('America/', ''));
  if (config.frequency === 'weekly' && config.daysOfWeek) {
    const dayNums = parseCsvNumbers(config.daysOfWeek);
    const dayNames = dayNums.map((d) => DAYS_OF_WEEK[d]?.label ?? String(d));
    parts.push(dayNames.join(', '));
  }
  if (config.frequency === 'monthly' && config.daysOfMonth) {
    const dayNums = parseCsvNumbers(config.daysOfMonth);
    const sorted = [...dayNums].sort((a, b) => a - b);
    parts.push(`Day ${sorted.join(', ')}`);
  }
  return parts.length > 0 ? parts.join(' | ') : '—';
}

function getRunStatusVariant(status: string): 'success' | 'warning' | 'destructive' | 'default' {
  return RUN_STATUS_VARIANT[status.toLowerCase()] ?? 'default';
}

// ── Form state ───────────────────────────────────────────────────────────────

interface FormState {
  tenantId: string;
  locationId: string;
  payeeId: string;
  frequency: string;
  scheduleTime: string;
  timezone: string;
  daysOfWeek: number[];
  daysOfMonth: number[];
  purpose: string;
  createdByName: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  tenantId: '',
  locationId: '',
  payeeId: '',
  frequency: 'daily',
  scheduleTime: '08:00',
  timezone: 'America/New_York',
  daysOfWeek: [],
  daysOfMonth: [],
  purpose: '',
  createdByName: '',
  isActive: true,
};

function configToForm(config: AutoPoConfig): FormState {
  return {
    tenantId: config.tenantId,
    locationId: config.locationId,
    payeeId: config.payeeId,
    frequency: config.frequency,
    scheduleTime: config.scheduleTime ?? '08:00',
    timezone: config.timezone ?? 'America/New_York',
    daysOfWeek: parseCsvNumbers(config.daysOfWeek),
    daysOfMonth: parseCsvNumbers(config.daysOfMonth),
    purpose: config.purpose ?? '',
    createdByName: config.createdByName ?? '',
    isActive: config.isActive,
  };
}

// ── Run History Sub-table ────────────────────────────────────────────────────

function RunHistoryPanel({ configId }: { configId: string }) {
  const [runPage, setRunPage] = useState(1);
  const { data, isLoading } = useAutoPoConfigRuns(configId, { page: runPage, perPage: 10 });
  const trigger = useTriggerAutoPoConfig();

  const runs: AutoPoConfigRun[] = data?.data ?? [];
  const pagination = data?.pagination;

  function handleTrigger() {
    trigger.mutate(configId, {
      onSuccess: () => toast.success('Auto PO triggered successfully'),
    });
  }

  return (
    <div className="bg-muted/20 border-t border-border px-6 py-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-foreground">Run History</h4>
        <button
          type="button"
          onClick={handleTrigger}
          disabled={trigger.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {trigger.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          Run Now
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-24 items-center justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <span className="text-sm">Loading runs...</span>
        </div>
      ) : runs.length === 0 ? (
        <div className="flex h-20 items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
          No runs recorded yet
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className="px-3 py-2.5 text-left text-[12px] font-semibold tracking-wide text-gray-700">
                    Status
                  </th>
                  <th className="px-3 py-2.5 text-left text-[12px] font-semibold tracking-wide text-gray-700">Items</th>
                  <th className="px-3 py-2.5 text-left text-[12px] font-semibold tracking-wide text-gray-700">PO #</th>
                  <th className="px-3 py-2.5 text-left text-[12px] font-semibold tracking-wide text-gray-700">
                    Started
                  </th>
                  <th className="px-3 py-2.5 text-left text-[12px] font-semibold tracking-wide text-gray-700">
                    Finished
                  </th>
                  <th className="px-3 py-2.5 text-left text-[12px] font-semibold tracking-wide text-gray-700">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                    <td className="px-3 py-2">
                      <Badge variant={getRunStatusVariant(run.status)}>
                        {run.status.charAt(0).toUpperCase() + run.status.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-sm text-foreground">{run.itemsCount}</td>
                    <td className="px-3 py-2 text-sm">
                      {run.orderNumber ? (
                        <span className="font-mono text-primary">{run.orderNumber}</span>
                      ) : run.purchaseOrderId ? (
                        <span className="font-mono text-primary text-xs">{run.purchaseOrderId.slice(0, 8)}...</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateTime(run.startedAt)}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {run.finishedAt ? formatDateTime(run.finishedAt) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[200px] truncate">
                      {run.skipReason ?? run.errorMessage ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-2">
              <DataTablePagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                perPage={pagination.perPage}
                onPageChange={setRunPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Create / Edit Modal ──────────────────────────────────────────────────────

interface ConfigFormModalProps {
  open: boolean;
  onClose: () => void;
  editConfig?: AutoPoConfig | null;
}

function ConfigFormModal({ open, onClose, editConfig }: ConfigFormModalProps) {
  const isEdit = !!editConfig;
  const [form, setForm] = useState<FormState>(editConfig ? configToForm(editConfig) : EMPTY_FORM);

  const createMutation = useCreateAutoPoConfig();
  const updateMutation = useUpdateAutoPoConfig();
  const saving = createMutation.isPending || updateMutation.isPending;

  // Tenant list for the dropdown
  const { data: tenantsData } = useAdminTenants({ perPage: 200, isActive: 'true' });
  const tenants: any[] = tenantsData?.data ?? [];

  // Filtered payees/locations based on selected tenant
  const { data: payeesData, isLoading: payeesLoading } = useTenantPayeesForAutoPo(form.tenantId);
  const { data: locationsData, isLoading: locationsLoading } = useTenantLocationsForAutoPo(form.tenantId);

  const payees = payeesData?.data ?? [];
  const locations = locationsData?.data ?? [];

  const tenantOptions = useMemo(() => tenants.map((t: any) => ({ value: t.id, label: t.name })), [tenants]);

  const locationOptions = useMemo(() => locations.map((l) => ({ value: l.id, label: l.name })), [locations]);

  const payeeOptions = useMemo(
    () =>
      payees.map((p) => ({
        value: p.id,
        label: p.email ? `${p.name} (${p.email})` : `${p.name} - no email`,
      })),
    [payees],
  );

  const selectedPayee = payees.find((p) => p.id === form.payeeId);
  const payeeHasNoEmail = selectedPayee && !selectedPayee.email;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleTenantChange(tenantId: string) {
    setForm((prev) => ({
      ...prev,
      tenantId,
      locationId: '',
      payeeId: '',
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validation
    if (!form.tenantId) {
      toast.error('Tenant is required');
      return;
    }
    if (!form.locationId) {
      toast.error('Location is required');
      return;
    }
    if (!form.payeeId) {
      toast.error('Vendor is required');
      return;
    }
    if (!form.frequency) {
      toast.error('Frequency is required');
      return;
    }
    if (form.frequency === 'weekly' && form.daysOfWeek.length === 0) {
      toast.error('Select at least one day of week');
      return;
    }
    if (form.frequency === 'monthly' && form.daysOfMonth.length === 0) {
      toast.error('Select at least one day of month');
      return;
    }

    try {
      if (isEdit && editConfig) {
        const payload: UpdateAutoPoConfigInput = {
          id: editConfig.id,
          payeeId: form.payeeId,
          locationId: form.locationId,
          frequency: form.frequency,
          scheduleTime: form.scheduleTime,
          timezone: form.timezone,
          daysOfWeek: form.frequency === 'weekly' ? form.daysOfWeek.join(',') : null,
          daysOfMonth: form.frequency === 'monthly' ? form.daysOfMonth.join(',') : null,
          purpose: form.purpose || null,
          createdByName: form.createdByName || undefined,
          isActive: form.isActive,
        };
        await updateMutation.mutateAsync(payload);
        toast.success('Configuration updated');
      } else {
        const payload: CreateAutoPoConfigInput = {
          tenantId: form.tenantId,
          payeeId: form.payeeId,
          locationId: form.locationId,
          frequency: form.frequency,
          scheduleTime: form.scheduleTime,
          timezone: form.timezone,
          daysOfWeek: form.frequency === 'weekly' ? form.daysOfWeek.join(',') : null,
          daysOfMonth: form.frequency === 'monthly' ? form.daysOfMonth.join(',') : null,
          purpose: form.purpose || null,
          createdByName: form.createdByName,
          isActive: form.isActive,
        };
        await createMutation.mutateAsync(payload);
        toast.success('Configuration created');
      }
      onClose();
    } catch {
      // Global mutation error handler shows toast
    }
  }

  // Reset form when modal opens/closes or editConfig changes
  // Using key on Modal is cleaner but we handle it manually for clarity
  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Auto PO Configuration' : 'Create Auto PO Configuration'}
      widthClass="max-w-[640px]"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit as any}
            disabled={saving}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Config'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Tenant */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Tenant</label>
          <SearchableSelect
            options={tenantOptions}
            value={form.tenantId}
            onChange={handleTenantChange}
            placeholder="Select a tenant..."
            searchPlaceholder="Search tenants..."
            disabled={isEdit}
            clearable={false}
          />
        </div>

        {/* Location */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Location</label>
          <SearchableSelect
            options={locationOptions}
            value={form.locationId}
            onChange={(v) => update('locationId', v)}
            placeholder={
              form.tenantId ? (locationsLoading ? 'Loading...' : 'Select a location...') : 'Select a tenant first'
            }
            searchPlaceholder="Search locations..."
            disabled={!form.tenantId || locationsLoading}
            clearable={false}
          />
        </div>

        {/* Vendor */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Vendor</label>
          <SearchableSelect
            options={payeeOptions}
            value={form.payeeId}
            onChange={(v) => update('payeeId', v)}
            placeholder={
              form.tenantId ? (payeesLoading ? 'Loading...' : 'Select a vendor...') : 'Select a tenant first'
            }
            searchPlaceholder="Search vendors..."
            disabled={!form.tenantId || payeesLoading}
            clearable={false}
          />
          {payeeHasNoEmail && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangle className="h-3 w-3" />
              <span>This vendor has no email on file. PO delivery may fail.</span>
            </div>
          )}
        </div>

        {/* Frequency */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Frequency</label>
          <select
            value={form.frequency}
            onChange={(e) => update('frequency', e.target.value)}
            className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          >
            {FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        {/* Schedule Time + Timezone row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Schedule Time</label>
            <input
              type="time"
              value={form.scheduleTime}
              onChange={(e) => update('scheduleTime', e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Timezone</label>
            <select
              value={form.timezone}
              onChange={(e) => update('timezone', e.target.value)}
              className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Days of Week (weekly only) */}
        {form.frequency === 'weekly' && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Days of Week</label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => {
                const num = Number(day.value);
                const isChecked = form.daysOfWeek.includes(num);
                return (
                  <label
                    key={day.value}
                    className={[
                      'inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors',
                      isChecked
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-border bg-white text-muted-foreground hover:bg-muted',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isChecked}
                      onChange={() => {
                        update(
                          'daysOfWeek',
                          isChecked ? form.daysOfWeek.filter((d) => d !== num) : [...form.daysOfWeek, num],
                        );
                      }}
                    />
                    {day.label}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Days of Month (monthly only) */}
        {form.frequency === 'monthly' && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Days of Month</label>
            <MultiSelect
              options={DAYS_OF_MONTH_OPTIONS}
              selected={form.daysOfMonth.map(String)}
              onChange={(vals) => update('daysOfMonth', vals.map(Number))}
              placeholder="Select days..."
              triggerLabel="Days"
            />
          </div>
        )}

        {/* Purpose */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Purpose</label>
          <input
            type="text"
            value={form.purpose}
            onChange={(e) => update('purpose', e.target.value)}
            placeholder="e.g. Weekly produce restock"
            className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          />
        </div>

        {/* Created By Name */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Created By Name</label>
          <input
            type="text"
            value={form.createdByName}
            onChange={(e) => update('createdByName', e.target.value)}
            placeholder="Name of the person setting this up"
            className="h-9 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          />
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
          <div>
            <p className="text-sm font-medium text-foreground">Active</p>
            <p className="text-xs text-muted-foreground">When active, POs will be generated on schedule</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.isActive}
            onClick={() => update('isActive', !form.isActive)}
            className={[
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              form.isActive ? 'bg-primary' : 'bg-gray-200',
            ].join(' ')}
          >
            <span
              className={[
                'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform',
                form.isActive ? 'translate-x-5' : 'translate-x-0',
              ].join(' ')}
            />
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export function AdminAutoPoPage() {
  const [page, setPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editConfig, setEditConfig] = useState<AutoPoConfig | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AutoPoConfig | null>(null);

  const { data, isLoading } = useAutoPoConfigs({ page, perPage: 20 });
  const deleteMutation = useDeleteAutoPoConfig();
  const configs: AutoPoConfig[] = data?.data ?? [];
  const pagination = data?.pagination;

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleEdit(config: AutoPoConfig) {
    setEditConfig(config);
  }

  function handleCloseModal() {
    setShowCreateModal(false);
    setEditConfig(null);
  }

  async function handleDelete(config: AutoPoConfig) {
    try {
      await deleteMutation.mutateAsync(config.id);
      toast.success('Configuration deleted');
      setConfirmDelete(null);
    } catch {
      // Global handler shows toast
    }
  }

  function getLastRunBadgeVariant(
    status: string | null | undefined,
  ): 'success' | 'warning' | 'destructive' | 'default' {
    if (!status) return 'default';
    return getRunStatusVariant(status);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Auto Purchase Orders"
        description="Manage automatic purchase order generation schedules for tenants."
      >
        <button
          type="button"
          onClick={() => {
            setEditConfig(null);
            setShowCreateModal(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Create Config
        </button>
      </PageHeader>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span className="text-sm">Loading configurations...</span>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className="w-8 px-3 py-3.5" />
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Tenant
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Location
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Vendor
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Frequency
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Schedule
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Active
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Last Run
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {configs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Settings2 className="h-10 w-10 text-muted-foreground/40" />
                        <p className="text-sm font-medium text-muted-foreground">No auto PO configurations</p>
                        <p className="text-xs text-muted-foreground">
                          Click "Create Config" to set up automatic purchase order generation.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  configs.map((config) => {
                    const isExpanded = expandedIds.has(config.id);
                    return (
                      <ConfigRow
                        key={config.id}
                        config={config}
                        isExpanded={isExpanded}
                        onToggle={() => toggleExpanded(config.id)}
                        onEdit={() => handleEdit(config)}
                        onDelete={() => setConfirmDelete(config)}
                        getLastRunBadgeVariant={getLastRunBadgeVariant}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination && (
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {/* Create / Edit Modal */}
      {(showCreateModal || editConfig) && (
        <ConfigFormModal
          key={editConfig?.id ?? 'create'}
          open={showCreateModal || !!editConfig}
          onClose={handleCloseModal}
          editConfig={editConfig}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Configuration"
        message={`Are you sure you want to delete the auto PO config for "${confirmDelete?.payeeName ?? 'this vendor'}" at "${confirmDelete?.locationName ?? 'this location'}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (confirmDelete) handleDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

// ── Config Row (extracted for readability) ───────────────────────────────────

interface ConfigRowProps {
  config: AutoPoConfig;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  getLastRunBadgeVariant: (status: string | null | undefined) => 'success' | 'warning' | 'destructive' | 'default';
}

function ConfigRow({ config, isExpanded, onToggle, onEdit, onDelete, getLastRunBadgeVariant }: ConfigRowProps) {
  return (
    <>
      <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
        {/* Expand toggle */}
        <td className="px-3 py-3 text-center">
          <button
            type="button"
            onClick={onToggle}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>

        {/* Tenant */}
        <td className="px-4 py-3 text-sm font-medium text-foreground">
          {config.tenantName ?? config.tenantId.slice(0, 8)}
        </td>

        {/* Location */}
        <td className="px-4 py-3 text-sm text-muted-foreground">
          {config.locationName ?? config.locationId.slice(0, 8)}
        </td>

        {/* Vendor */}
        <td className="px-4 py-3 text-sm text-muted-foreground">{config.payeeName ?? config.payeeId.slice(0, 8)}</td>

        {/* Frequency */}
        <td className="px-4 py-3 text-sm text-foreground">{formatFrequency(config.frequency)}</td>

        {/* Schedule */}
        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatScheduleSummary(config)}</td>

        {/* Active */}
        <td className="px-4 py-3">
          <Badge variant={config.isActive ? 'success' : 'default'}>{config.isActive ? 'Active' : 'Inactive'}</Badge>
        </td>

        {/* Last Run */}
        <td className="px-4 py-3">
          {config.lastRun ? (
            <div className="flex flex-col gap-0.5">
              <Badge variant={getLastRunBadgeVariant(config.lastRun.status)}>
                {config.lastRun.status.charAt(0).toUpperCase() + config.lastRun.status.slice(1)}
              </Badge>
              <span className="text-[10px] text-muted-foreground">{formatDateTime(config.lastRun.startedAt)}</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">Never</span>
          )}
        </td>

        {/* Actions */}
        <td className="px-4 py-3">
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
              title="Edit"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded run history */}
      {isExpanded && (
        <tr>
          <td colSpan={9} className="p-0">
            <RunHistoryPanel configId={config.id} />
          </td>
        </tr>
      )}
    </>
  );
}
